import { Page } from '@root/pages/Type';
import { MacroInitialValues } from '@pages/Ui/Payments/Topup/Macro/Index';
import { axiosWithStore } from '@root/utils/axios';
import withCheckAuth from '@root/utils/withCheckAuth';
import { useTranslation, Trans } from 'react-i18next';
import { Router } from '@root/router';
import {
    MacroFormValues,
    MacroServerResponse,
} from '@root/pages/Ui/Payments/Topup/Types';
import PrettyError from '@root/utils/errors/PrettyError';
import NotificationError from '@root/utils/errors/NotificationError';
import styles from './style.module.scss';
import getConfig from 'next/config';
import { useFormRequest } from '@root/utils/useFormRequest';
import Link from '@components/Link';
import useRouteHref from '@root/utils/useRouteHref';
import Field from '@root/components/Form/Field';
import withForm, { withFormProps } from '@root/utils/withForm';
import useMacroMiddleware from '@root/pages/Ui/Payments/Topup/Macro/useMacroMiddleware';
import { ErrorMessage } from '@root/components/Form/Index';
import MacroContainer from '@root/pages/Ui/Payments/Topup/Macro/MacroContainer';
import { init as ValidationSchemaInit } from './Validation';
import getPropertyFromCtx from '@root/utils/getPropertyFromCtx';
import { LiveHelpProvider } from '@root/components/LiveHelpContext';

const { publicRuntimeConfig } = getConfig();

type UserTopupFormValues = MacroFormValues & {
    payer: string;
};

const initialValues: UserTopupFormValues = {
    ...MacroInitialValues,
    payer: '',
};

export type UserTopupProps = withFormProps<UserTopupFormValues> & {
    invalidRequest: boolean;
    name?: string;
    hash: string;
};

type UserTopupPageData = {
    name?: string;
    invalidRequest?: boolean;
};

export type UserTopupSubmitResponse = MacroServerResponse & {
    invalidAmount?: boolean;
    hasPayed?: boolean;
    redirect?: string;
    invalidRequest?: boolean;
    methodNotFound?: boolean;
    invalidMethod?: boolean;
};

const Index: Page<UserTopupProps> = (props) => {
    const { invalidRequest, name, hash, errorMessage, handleForm, ...rProps } =
        props;
    const { t } = useTranslation('extraPaymentsUserTopup');

    const successUrl = useRouteHref('ui_payments_user_topup_success');
    const cancelUrl = useRouteHref('ui_payments_user_topup_cancel');

    const { formHandle } = useFormRequest<
        UserTopupFormValues,
        UserTopupSubmitResponse
    >(
        {
            url: `${publicRuntimeConfig.API_URL}/public/userTopup/${hash}`,
            values: (value) => ({
                ...value,
                successUrl: publicRuntimeConfig.HOST_URL + successUrl,
                cancelUrl: publicRuntimeConfig.HOST_URL + cancelUrl,
            }),
        },
        (response) => {
            if (response.invalidRequest) {
                throw new NotificationError(
                    t('macro.errors.invalid_request', {
                        defaultValue: 'Klaidingai nurodyta suma.',
                    }),
                );
            }
            if (response.invalidAmount) {
                throw new NotificationError(
                    t('macro.errors.invalid_amount', {
                        defaultValue: 'Klaidingai nurodyta suma.',
                    }),
                );
            }
            if (response.methodNotFound) {
                throw new NotificationError(
                    t('macro.errors.method_not_found', {
                        defaultValue: 'Nurodytas būdas nerasta.',
                    }),
                );
            }
            if (response.invalidMethod) {
                throw new NotificationError(
                    t('macro.errors.method_not_found', {
                        defaultValue: 'Nurodytas klaidingas būdas.',
                    }),
                );
            }
            if (response.redirect) {
                return Router.push(response.redirect);
            }
            if (!response.success) {
                throw new PrettyError(
                    t('macro.errors.fatalError', {
                        defaultValue: 'Klaida. Prašome mėginti dar kartą.',
                    }),
                );
            }
        },
        t('macro.errors.fatalError', {
            defaultValue: 'Klaida. Prašome mėginti dar kartą.',
        }),
    );

    const formHandleWithMiddleware =
        useMacroMiddleware<UserTopupFormValues>(formHandle);

    if (invalidRequest) return <p>Hash not found...</p>;

    return (
        <LiveHelpProvider>
            <div className={styles.container}>
                <div className={styles.logo_container}>
                    <Link href="landing_home">
                        <a>
                            <img src="/static/img/logos/logo.svg" alt="logo" />
                        </a>
                    </Link>
                </div>

                <h1 className={`display-1 text-center ${styles.title}`}>
                    <Trans i18nKey="payment.title" ns="extraPaymentsUserTopup">
                        Vartotojo balanso pildymas
                    </Trans>
                </h1>

                <div className="container">
                    <form onSubmit={handleForm(formHandleWithMiddleware)}>
                        <p className={`text-center ${styles.description}`}>
                            <Trans
                                i18nKey="payment.description"
                                ns="extraPaymentsUserTopup"
                                values={{ name }}
                            >
                                Jus papildysite `[[name]]` vartotojo balansa.
                            </Trans>
                        </p>

                        <div className={styles.order_summary}>
                            <ErrorMessage errorMessage={errorMessage} />

                            <MacroContainer
                                {...rProps}
                                fieldRow={
                                    <div className="form-group col-sm-12 col-lg-6">
                                        <Field
                                            name="payer"
                                            label={t('form.payer', {
                                                defaultValue: 'Mokėtojas',
                                            })}
                                            type="text"
                                        />
                                    </div>
                                }
                                isBlackOption
                                initialValues={initialValues}
                                methods={[]}
                                defaultMethod={0}
                            />
                        </div>
                    </form>
                </div>
            </div>
        </LiveHelpProvider>
    );
};

export const getServerSideProps = withCheckAuth(async (ctx) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const hash = getPropertyFromCtx(ctx, 'hash');
    if (typeof hash === 'undefined') {
        throw new Error('Invalid request. (required payment hash)');
    }
    const { store } = ctx;
    const axios = axiosWithStore(store);
    const context = await axios
        .get<UserTopupPageData>(`/public/userTopup/${hash}`)
        .then((response) => ({
            invalidRequest: false,
            ...response.data,
        }))
        .catch(() => ({
            invalidRequest: true,
        }));
    return {
        props: {
            hash,
            ...context,
        },
    };
});

export default withForm<UserTopupFormValues>({
    initialValues,
    validationSchema: () => ValidationSchemaInit(),
    enableReinitialize: true,
})(Index);
